import { useEffect, useRef, useState, type ReactNode } from "react";
import { api } from "../api";
import type { BootstrapResponse, SettlementBatchSummary, SettlementRecord } from "../types";
import { today, type SettlementTab } from "../payrollPresentation";
import { PayrollSelectionBar } from "./PayrollPending";
import { PayrollRecords } from "./PayrollRecords";
import { PayrollBatches } from "./PayrollBatches";
import { PayrollBatchHistory } from "./PayrollBatchHistory";
import { PayrollManualEditor } from "./PayrollManualEditor";
import { PayrollBatchCreation } from "./PayrollBatchCreation";
import { PayrollCompleteDialog } from "./PayrollCompleteDialog";
import { PayrollSmsLoader } from "./PayrollSmsLoader";
import { PayrollPagination } from "./PayrollPagination";
import { RetainedContent } from "./RetainedContent";
import { type ColumnFilterValues, usePayrollData, type SettlementSort } from "./usePayrollData";
import type { MonthRange } from "./PeriodFilter";
import { useDraftLeaveGuard } from "./useDraftLeaveGuard";
import { Button } from "./ui/Button";
import { Feedback, Panel } from "./ui/Surface";
import { usePlanningBaseline } from "./ui/DesignSystem";
import { PlanningPayrollWorkspace } from "./PlanningPayrollWorkspace";
import "./ui/Chip.css";
import "./PayrollManagement.css";

export type { SettlementTab } from "../payrollPresentation";
type Creation = { suggestedName: string; records: SettlementRecord[] };

/* 업무위탁 계약서는 그 사람과 회사 사이의 문서라 정산 한 줄에 딸린 첨부가 아니다.
   작업자 상세의 민감정보로 옮길 때까지 PayrollAttachments는 연결하지 않는다. */
type SettlementProps = { data: BootstrapResponse; actorName: string; range: MonthRange };
/** 창이 보여 주는 둘. 완료 버튼이 그 경계다 — 누르기 전은 예정, 누른 뒤는 내역. */
type Stage = "due" | "done";

/**
 * 고를 수 있는 값은 고르면 뭔가 나오는 값이어야 한다. 그래서 목록을 명부가 아니라 지금 표에
 * 실제로 찍힌 값에서 뽑는다 — 표는 이미 다른 열의 조건을 다 거친 결과라, 프로젝트를 고르면
 * 작업 목록이 그 프로젝트 것만 남고 작업자도 그 프로젝트에서 일한 사람만 남는다.
 */
const ledgerNames = (records: SettlementRecord[]) => {
  const pull = (pick: (record: SettlementRecord) => string | null | undefined) =>
    [...new Set(records.map(pick).filter((value): value is string => !!value))]
      .sort((a, b) => a.localeCompare(b, "ko-KR")).map(value => ({ value, label: value }));
  const workers = new Map(records.map(record => [record.workerId, record.workerName]));
  return {
    projectName: pull(record => record.projectName),
    subtaskName: pull(record => record.subtaskName),
    workerId: [...workers].sort((a, b) => a[1].localeCompare(b[1], "ko-KR")).map(([value, label]) => ({ value, label })),
  };
};

export function PayrollWorkspace(props: SettlementProps & {
  /* 열 필터와 정렬은 각 표가 독립적으로 관리한다. */
  /* 어느 쪽을 볼지 바깥에서 정해 줄 수 있다 — 요약 카드의 손잡이가 창을 열면서 함께 말한다. */
  stage?: Stage;
  onStage?: (stage: Stage) => void;
  /* 안쪽 창의 X가 겹친 창을 한 번에 걷게 한다 — 뒤로와 닫기가 같은 일을 하면 둘 중
     하나는 거짓말이 된다. */
  onCloseAll?: () => void;
}) {
  const baseline = usePlanningBaseline();
  return baseline ? <PlanningPayrollWorkspace {...props} /> : <ManagementPayrollWorkspace {...props} />;
}

function ManagementPayrollWorkspace(props: SettlementProps & { stage?: Stage; onStage?: (stage: Stage) => void }) {
  const { data, actorName, range } = props;
  const baseline = usePlanningBaseline();
  const [typedQuery, setTypedQuery] = useState("");
  const [query, setQuery] = useState("");
  useEffect(() => { const timer = window.setTimeout(() => setQuery(typedQuery.trim()), 220); return () => window.clearTimeout(timer); }, [typedQuery]);
  /* 구분은 표의 열이 말한다. 필터에서 빼는 대신 질의는 늘 전체 구분을 가져온다. */
  const activeTab = "all" as const;
  /* 정산에는 세 상태가 있고 코드에도 이름이 붙어 있다 — 미정산 · 정산 대기 · 정산 완료.
     앞의 둘은 아직 되돌릴 수 있어 "할 일"이고 마지막은 지나간 일이다. 한 표에 섞어 두면
     절반의 체크박스가 죽어 있는 이유가 화면 어디에도 안 적힌다. */
  const [ownStage, setOwnStage] = useState<Stage>("due");
  /* 바깥에서 정해 줄 수도 있다 — 요약 카드의 "정산 예정 / 정산 내역" 손잡이가 창을 열면서
     어느 쪽을 볼지 함께 말한다. */
  const stage = props.stage ?? ownStage;
  const setStage = props.onStage ?? setOwnStage;
  const [selectedBatch, setSelectedBatch] = useState<SettlementBatchSummary | null>(null);
  const [historyCursors, setHistoryCursors] = useState([""]);
  const [historyPage, setHistoryPage] = useState(0);
  const stageStatus = stage === "due" ? (["pending"] as const) : (["paid"] as const);
  const [tableSettings, setTableSettings] = useState<Record<string, { sort: SettlementSort; filters: ColumnFilterValues }>>({});
  const tableKey = stage === "due" ? "pending" : `batch-${selectedBatch?.id ?? ""}`;
  const batchTableKey = `batch-${selectedBatch?.id ?? ""}`;
  const settings = (key: string) => tableSettings[key] ?? { sort: "work_time" as const, filters: {} };
  const { sort, filters: columnFilters } = settings(tableKey);
  const state = usePayrollData(activeTab, range, stage === "done" ? selectedBatch?.id : undefined, sort, columnFilters, stageStatus, false,
    stage === "due" ? "prepared" : "paid", stage === "done" ? historyCursors[historyPage] || undefined : undefined, stage === "done" ? 10 : 20, true, baseline ? query : "");
  // 예정 화면의 미묶음 목록은 선택한 묶음과 독립적이다. 묶음을 열어도 조회 결과와 선택이 유지된다.
  const batchRecords = usePayrollData(activeTab, range, stage === "due" ? selectedBatch?.id : undefined, settings(batchTableKey).sort, settings(batchTableKey).filters,
    ["batched"], false, "prepared", undefined, 20, stage === "due" && !!selectedBatch, baseline ? query : "", true);
  const [selectionState, setSelection] = useState<{ tab: SettlementTab; records: SettlementRecord[] }>({ tab: activeTab, records: [] });
  const selection = selectionState.tab === activeTab ? selectionState.records : [];
  const selectionScope = useRef(state.key); selectionScope.current = state.key;
  const selectionRequest = useRef<AbortController | null>(null);
  const [wholeSelection, setWholeSelection] = useState<{ key: string; loading: boolean; ids: string[] | null; error: string } | null>(null);
  const selectingAll = wholeSelection?.key === state.key && wholeSelection.loading;
  useEffect(() => () => { selectionRequest.current?.abort(); selectionRequest.current = null; }, [state.key, stage]);
  const selectAll = async (checked: boolean) => {
    if (!checked) { setSelection({ tab: activeTab, records: [] }); setWholeSelection(null); return; }
    if (selectionRequest.current) return;
    const controller = new AbortController(), key = state.key;
    selectionRequest.current = controller;
    setWholeSelection({ key, loading: true, ids: null, error: "" });
    try {
      const records = await state.loadAllPending(controller.signal);
      if (controller.signal.aborted || selectionScope.current !== key) return;
      setSelection({ tab: activeTab, records });
      setWholeSelection({ key, loading: false, ids: records.map(record => record.id), error: "" });
    } catch (cause) {
      if (!controller.signal.aborted && selectionScope.current === key) setWholeSelection({ key, loading: false, ids: null,
        error: cause instanceof Error ? cause.message : "미정산 항목 전체를 불러오지 못했습니다." });
    } finally { if (selectionRequest.current === controller) selectionRequest.current = null; }
  };
  const selectionIds = new Set(selection.map(record => record.id));
  const pendingIds = wholeSelection?.key === state.key && wholeSelection.ids !== null ? wholeSelection.ids
    : !state.records.nextCursor ? state.records.items.filter(record => record.status === "pending").map(record => record.id) : null;
  const allSelected = !!pendingIds?.length && pendingIds.every(id => selectionIds.has(id));
  /* 이름은 "오늘이 몇 월인가"가 아니라 "무엇을 정산하는가"에서 나온다. 8월 작업을 9월에
     정산하면 오늘 기준 이름은 "2026년 09월 묶음"이 되어, 8월 칩에서 9월 묶음이 뜨는 것처럼
     보였다. 고른 기록의 정산일을 따른다. */
  const namePlaceholder = (() => {
    const months = [...new Set(selection.map(record => record.settlementDate.slice(0, 7)))].sort();
    const base = months.length === 1 ? months[0] : range?.from.slice(0, 7) ?? today().slice(0, 7);
    return `${base.replace("-", "년 ")}월 정산 묶음`;
  })();
  const [notice, setNotice] = useState<{ text: string; error?: boolean } | null>(null);
  const [register, setRegister] = useState(false), [creation, setCreation] = useState<Creation | null>(null), [completion, setCompletion] = useState<SettlementBatchSummary | null>(null), [removingRecordId, setRemovingRecordId] = useState(""), [sms, setSms] = useState<SettlementBatchSummary | null>(null);
  const [downloading, setDownloading] = useState(false), downloadGuard = useRef(false), root = useRef<HTMLDivElement>(null), returnFocus = useRef<HTMLElement | null>(null), retry = useRef<HTMLButtonElement>(null);
  const writeDisabled = state.loading || !!state.error || !!creation || downloading || !!completion || !!removingRecordId || selectingAll;
  /* 나갈 때 되묻지 않는다. 표에서 몇 줄 고른 것은 버려도 다시 고르면 그만이고, 이 화면의
     다른 동작들도 확인 없이 바로 처리한다. 묶음을 만드는 중일 때만 막는다. */
  useDraftLeaveGuard({ dirty: false, busy: downloading || !!creation });
  useEffect(() => { setSelection({ tab: activeTab, records: [] }); setSelectedBatch(null); setHistoryCursors([""]); setHistoryPage(0); setNotice(null); }, [range?.from, range?.to]);
  useEffect(() => {
    if (stage !== "done" || state.loading || state.error) return;
    if (state.batches.items.length && !state.batches.items.some(batch => batch.id === selectedBatch?.id)) setSelectedBatch(state.batches.items[0]);
    if (!state.batches.items.length && selectedBatch) setSelectedBatch(null);
  }, [stage, state.loading, state.error, state.batches.items, selectedBatch]);
  const select = (ids: string[]) => { const records = new Map([...selection, ...state.records.items].map(record => [record.id, record])); setSelection({ tab: activeTab, records: [...new Set(ids)].flatMap(id => records.has(id) ? [records.get(id)!] : []) }); };
  const remember = () => { returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null; setNotice(null); };
  const restoreFocus = () => state.error ? retry.current : returnFocus.current?.isConnected && !returnFocus.current.matches(":disabled") ? returnFocus.current : root.current?.querySelector<HTMLButtonElement>('.payroll-source-filter .ui-chip.selected') ?? null;
  const notify = (text: string, error?: boolean) => setNotice({ text, error });
  const download = async (batch: SettlementBatchSummary) => { if (downloadGuard.current) return; downloadGuard.current = true; setDownloading(true); setNotice(null); try { await api.downloadSettlementTransferFile(batch.id); } catch (cause) { notify(cause instanceof Error ? cause.message : "이체 파일을 내려받지 못했습니다.", true); } finally { downloadGuard.current = false; setDownloading(false); } };
  /* 미정산 한 줄을 지우는 일이라 확인 창을 두지 않는다. 이미 묶인 줄은 버튼이 아예 없고,
     잘못 지워도 수동 등록으로 다시 넣으면 되는 되돌릴 수 있는 일이다. 계약은 첨부 파일이
     함께 사라지므로 그것만 한 번 묻는다. */
  const deleteRecord = async (record: SettlementRecord) => {
    if (record.source === "contract" && record.recordKind === "base"
      && !window.confirm(`${record.title}을(를) 삭제하면 연결된 계약 파일도 함께 사라집니다.\n삭제할까요?`)) return;
    setNotice(null);
    try {
      await (record.recordKind === "manual" ? api.deleteManualSettlement(record.id)
        : record.recordKind === "adjustment" ? api.deleteSettlementAdjustment(record.id)
        : record.source === "part_time" ? api.deletePartTimeSettlement(record.referenceId)
        : api.deleteSettlementContract(record.referenceId));
      select(selection.filter(item => item.id !== record.id).map(item => item.id));
      await state.reload();
    } catch (cause) { notify(cause instanceof Error ? cause.message : "정산 내역을 삭제하지 못했습니다.", true); }
  };
  const removeRecordFromBatch = async (record: SettlementRecord) => {
    if (!record.batchId || removingRecordId) return;
    setRemovingRecordId(record.id); setNotice(null);
    try {
      const result = await api.removeSettlementBatchItem(record.batchId, record.id);
      if (result.batchDeleted) setSelectedBatch(null);
      try { await Promise.all([state.reload(), ...(result.batchDeleted ? [] : [batchRecords.reload()])]); }
      catch { notify("항목은 묶음에서 제외했지만 최신 목록을 불러오지 못했습니다. 목록을 다시 확인해 주세요.", true); }
    } catch (cause) { notify(cause instanceof Error ? cause.message : "정산 항목을 묶음에서 제외하지 못했습니다.", true); }
    finally { setRemovingRecordId(""); }
  };
  const paging = (collection: "records" | "batches", label: string, source = state) => <PayrollPagination hasMore={!!source[collection].nextCursor} label={label} loading={source.paging[collection]?.loading} error={source.paging[collection]?.error} disabled={source.loading || !!source.error || selectingAll} onLoad={() => source.loadMore(collection)} />;
  const recordList = (source = state, mode: "pending" | "batch" | "history" = stage === "due" ? "pending" : "history") => {
    const key = mode === "batch" ? batchTableKey : tableKey;
    const current = settings(key);
    return <><PayrollRecords toolbarLead={baseline && mode === "pending" ? selectionBar() : undefined} filters={{
    values: current.filters,
    options: source.records.filterOptions ?? ledgerNames(source.records.items),
    onChange: (field, value) => setTableSettings(previous => ({ ...previous, [key]: { ...current, filters: { ...current.filters, [field]: value } } })),
  }} emptyText={source.error ? "목록을 다시 확인해 주세요." : undefined} records={source.records.items} showSource={activeTab === "all"} disabled={writeDisabled || source.loading || !!source.error} sort={current.sort} onSort={value => setTableSettings(previous => ({ ...previous, [key]: { ...current, sort: value } }))} onDelete={record => void deleteRecord(record)}
    selection={mode === "pending" ? { workers: data.workers, selectedIds: selection.map(record => record.id), onSelect: select,
      allSelected, onSelectAll: checked => void selectAll(checked) } : undefined}
    onRemoveFromBatch={mode === "batch" ? record => void removeRecordFromBatch(record) : undefined} />{paging("records", "더 보기", source)}</>;
  };
  const selectionBar = () => <PayrollSelectionBar selected={selection} workers={data.workers} disabled={writeDisabled}
    onRegister={() => { remember(); setRegister(true); }}
    onCreate={() => { remember(); setCreation({ suggestedName: namePlaceholder, records: [...selection] }); }} />;
  const batchList = () => <PayrollBatches batches={state.batches.items} selectedId={selectedBatch?.id} disabled={writeDisabled} onSelect={batch => setSelectedBatch(selectedBatch?.id === batch.id ? null : batch)} onDownload={batch => void download(batch)} onSms={batch => { remember(); setSms(batch); }} onComplete={batch => { remember(); setCompletion(batch); }}
    renderExpanded={() => recordList(batchRecords, "batch")}
    onDelete={batch => void (async () => {
      if (!window.confirm(`${batch.name}을(를) 삭제하면 ${batch.recordCount.toLocaleString()}건이 미정산으로 돌아갑니다.\n삭제할까요?`)) return;
      try { await api.deleteSettlementBatch(batch.id); setSelectedBatch(current => current?.id === batch.id ? null : current); await state.reload(); }
      catch (cause) { notify(cause instanceof Error ? cause.message : "정산 묶음을 삭제하지 못했습니다.", true); }
    })()}
    onRename={(batch, name) => void (async () => {
      try { await api.renameSettlementBatch(batch.id, name); await state.reload(); }
      catch (cause) { notify(cause instanceof Error ? cause.message : "묶음명을 바꾸지 못했습니다.", true); }
    })()} />;
  const history = () => <PayrollBatchHistory batches={state.batches.items} selected={state.batches.items.find(batch => batch.id === selectedBatch?.id) ?? null}
    page={historyPage} hasNext={!!state.batches.nextCursor} disabled={writeDisabled}
    onSelect={setSelectedBatch}
    onPrevious={() => { if (historyPage === 0) return; setSelectedBatch(null); setHistoryPage(page => page - 1); }}
    onNext={() => { if (!state.batches.nextCursor) return; setSelectedBatch(null); setHistoryCursors(cursors => [...cursors.slice(0, historyPage + 1), state.batches.nextCursor!]); setHistoryPage(page => page + 1); }}
    onDownload={batch => void download(batch)} onSms={batch => { remember(); setSms(batch); }}
    onRename={(batch, nextName) => void (async () => { try { await api.renameSettlementBatch(batch.id, nextName); await state.reload(); } catch (cause) { notify(cause instanceof Error ? cause.message : "묶음명을 바꾸지 못했습니다.", true); } })()}
    renderRecords={() => recordList(state, "history")} />;
  return <div ref={root} className="payroll-workspace" data-stage={stage} aria-busy={state.loading || selectingAll || undefined}>
    <div className="payroll-content">
      {/* 알림은 오류에만 쓴다. 잘된 일은 화면이 바뀌는 것으로 이미 보인다. */}
      {notice?.error && <Feedback tone="error">{notice.text}</Feedback>}
      {downloading && <Feedback>대량이체 파일을 내려받고 있습니다…</Feedback>}
      {stage === "due" && selectingAll && <Feedback>미정산 항목 전체를 불러오고 있습니다…</Feedback>}
      {stage === "due" && wholeSelection?.key === state.key && wholeSelection.error && <Feedback tone="error">
        {wholeSelection.error} <Button onClick={() => void selectAll(true)}>전체 선택 다시 시도</Button></Feedback>}
      {stage === "due" && !selectingAll && selection.length > 0 && <Feedback>
        미정산 항목 {selection.length.toLocaleString()}건 선택됨</Feedback>}
      {state.error && !register && !creation && <Feedback tone="error">{state.error} 최신 목록을 확인한 뒤 정산을 진행해 주세요. <Button ref={retry} busy={state.loading} onClick={() => void state.reload().catch(() => undefined)}>목록 다시 확인</Button></Feedback>}
      {stage === "due" && selectedBatch && batchRecords.error && <Feedback tone="error">{batchRecords.error} <Button busy={batchRecords.loading} onClick={() => void batchRecords.reload().catch(() => undefined)}>묶음 다시 확인</Button></Feedback>}
      <RetainedContent ready={state.ready
        && !(stage === "due" && selectedBatch && !batchRecords.ready)
        && !(stage === "done" && state.batches.items.length > 0 && !state.batches.items.some(batch => batch.id === selectedBatch?.id))}>
      <Panel>
        {/* 위에서 아래로 정산의 순서가 그대로 읽히게 둔다 — 등록·묶기부터 시작하고, 그렇게 만들어진
            묶음과 마지막 단계인 정산 완료가 그 아래다. 묶음은 여전히 표 바로 위라, 골라서
            좁혀 보면 결과가 바로 아래에 나타난다. */}
        <div className={baseline ? "payroll-head-row" : "payroll-toolbar"}>
          <div className="payroll-stage-chips" role="group" aria-label="정산 상태">
            {/* 전체는 두지 않는다 — 예정과 내역은 하는 일이 달라서(고르고 묶기 / 지나간 것 찾기)
                섞어 놓으면 다시 지금처럼 어느 줄을 만질 수 있는지가 흐려진다. */}
            {([["due", "정산 예정"], ["done", "정산 내역"]] as const).map(([value, label]) =>
              <button key={value} type="button" className={`ui-chip${stage === value ? " selected" : ""}`}
                aria-pressed={stage === value} onClick={() => { setStage(value); setSelectedBatch(null); setHistoryCursors([""]); setHistoryPage(0); }}>{label}</button>)}
          </div>
          {/* 등록·묶기는 예정에서만 할 일이다 — 내역에 두면 고를 줄이 하나도 없는 손잡이가 선다. */}
          {!baseline && stage === "due" && selectionBar()}
          {baseline && <label className="payroll-head-search">
            <svg viewBox="0 0 24 24" aria-hidden="true" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
            <input type="text" value={typedQuery} onChange={event => setTypedQuery(event.target.value)} placeholder="Search..." aria-label="찾기" />
          </label>}
        </div>
        {/* 아직 지급하지 않은 묶음은 예정에 남아 이체 파일과 정산 완료까지 이어진다. */}
        {stage === "due" && <>{batchList()}{paging("batches", "묶음 더 보기")}</>}
        {/* 완료된 묶음은 이름 인디케이터로 열 개씩 탐색한다. */}
        {stage === "done" && history()}
        {stage === "due" && recordList()}
      </Panel>
      </RetainedContent>
    </div>
    {register && <PayrollManualEditor workers={data.workers} actorName={actorName} refresh={state.reload}
      onSaved={() => setRegister(false)} onClose={() => setRegister(false)} restoreFocus={restoreFocus} />}
    {creation && <PayrollBatchCreation {...creation} actorName={actorName} refresh={state.reload} onCreated={() => setSelection({ tab: activeTab, records: [] })} onFinished={() => setCreation(null)} onClose={() => setCreation(null)} restoreFocus={restoreFocus} />}
    {completion && <PayrollCompleteDialog summary={completion} actorName={actorName} refresh={state.reload}
      onFinished={text => { setSelectedBatch(current => current?.id === completion.id ? null : current); setCompletion(null); notify(text); }}
      onClose={() => setCompletion(null)} onSms={() => { setSms(completion); setCompletion(null); }} restoreFocus={restoreFocus} />}
    {sms && <PayrollSmsLoader key={sms.id} summary={sms} refresh={state.reload} onClose={() => setSms(null)} notify={notify} />}
  </div>;
}
